/**
 * @graphql cc
 */

import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import React, { useEffect } from 'react';

import { usePageContentId } from '@confluence/page-context';
import { fg } from '@confluence/feature-gating';

import { useSendAnalyticsEvents } from '../analytics/useSendAnalyticsEvents';

import type { ContentType } from './ConsumptionEvents';
import type { SendConsumptionViewedEventQuery } from './__types__/SendConsumptionViewedEventQuery';

interface SendConsumptionViewedEventProps {
	spaceKey: string;
	contentType: ContentType;
	source: string;
}

export const SendConsumptionViewedEventExperiment = ({
	spaceKey,
	contentType,
	source,
}: SendConsumptionViewedEventProps) => {
	const [contentId] = usePageContentId();
	const { sendEvent } = useSendAnalyticsEvents();

	const {
		data: spaceData,
		loading,
		error,
	} = useQuery<SendConsumptionViewedEventQuery>(
		gql`
			query SendConsumptionViewedEventQuery($spaceKey: String!) {
				space(key: $spaceKey) {
					id
					key
					type
					status
				}
			}
		`,
		{
			variables: {
				spaceKey,
			},
			skip: !contentId,
		},
	);

	useEffect(() => {
		if (spaceData?.space && !loading && !error) {
			sendEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'viewed',
					actionSubject: 'consumption',
					actionSubjectId: 'client',
					source,
					attributes: {
						contentType,
						contentMode: 'classic',
						contentState: 'published',
						spaceId: spaceData.space.id ?? '',
						contentId: contentId ?? '',
					},
				},
			});
		}
	}, [spaceData, loading, error, contentId, sendEvent, contentType, source]);

	return null;
};

export const SendConsumptionViewedEvent = (props: SendConsumptionViewedEventProps) => {
	if (fg('_cc_unified_analytics_send_consumption_event_viewed')) {
		return <SendConsumptionViewedEventExperiment {...props} />;
	}

	return null;
};
